import {
  truncateChipDisplay,
  UNIFIED_TEXT_SEARCH_CHIP_LABEL,
} from '@/components/filters/filter-styles'
import { SegmentedFilterChip } from '@/components/filters/segmented-filter-chip'

export type UnifiedFilterChipProps = {
  label: string
  /** Operator word rendered between label and value (e.g. "is", "is not"). Omit when none. */
  operator?: string
  /** Accessible hint for the next operator when toggling. */
  nextOperatorLabel?: string
  value: string
  /** Full committed value for tooltip when `value` is a shortened display. */
  valueTitle?: string
  /** Opens the bar's picker for this filter (value segment). */
  onClick: () => void
  /**
   * When provided, the label segment becomes a control that flips the
   * operator (is ↔ is not) in place, Shopify-style. Omit for filters
   * without operator support — the label segment then opens the picker.
   */
  onToggleOperator?: () => void
  onRemove: () => void
  /** Committed keyword search — muted tint, single label segment, truncated value. */
  variant?: 'filter' | 'text'
  className?: string
}

/**
 * `SegmentedFilterChip` wired for the UnifiedFilterBar field: operator wording
 * on the label segment, picker opening on the value segment. No Popover of its
 * own — clicking asks the bar to open its single dropdown in picker mode.
 */
export function UnifiedFilterChip({
  label,
  operator,
  nextOperatorLabel,
  value,
  valueTitle,
  onClick,
  onToggleOperator,
  onRemove,
  variant = 'filter',
  className,
}: UnifiedFilterChipProps) {
  const isText = variant === 'text'
  const displayValue = isText ? truncateChipDisplay(value) : value
  const chipLabel = isText ? label || UNIFIED_TEXT_SEARCH_CHIP_LABEL : label
  const labelSegment =
    operator !== undefined
      ? isText && !onToggleOperator
        ? chipLabel
        : `${chipLabel} ${operator}`
      : chipLabel
  const valueTitleResolved =
    valueTitle ??
    (isText && value.length > displayValue.length
      ? value
      : !isText && value.length > 0
        ? value
        : undefined)
  const nextOperator = nextOperatorLabel ?? operator

  return (
    <SegmentedFilterChip
      data-unified-chip=""
      className={className}
      tint={isText ? 'muted' : 'primary'}
      preventFocusSteal
      label={labelSegment}
      value={displayValue}
      valueTitle={valueTitleResolved}
      onLabelClick={() => {
        if (onToggleOperator) onToggleOperator()
        else onClick()
      }}
      onValueClick={onClick}
      onRemove={onRemove}
      labelAriaLabel={
        onToggleOperator
          ? `${labelSegment}. Click to switch to ${nextOperator}.`
          : isText
            ? `${chipLabel} ${value}. Click to edit search.`
            : `${labelSegment} ${value}. Click to edit filter.`
      }
      labelTitle={onToggleOperator ? `Switch to “${nextOperator}”` : undefined}
      valueAriaLabel={
        isText
          ? `${labelSegment} ${value}. Click to edit search.`
          : `${labelSegment} ${value}. Click to edit filter.`
      }
      removeAriaLabel={isText ? 'Remove keyword search' : `Remove ${label} filter`}
    />
  )
}
